/**
 * client/src/components/forensic-recovery-lift-gate-panel.tsx
 *
 * #3042 adversarial review round 1, BLOCK 2 -- "unknown is not the same as
 * not-gated". While the ward's own read of whether this incident's lift
 * ceremony is question-gated is still pending or has errored, the caller
 * MUST NOT fall through to an ungated `onStartPath`/`onRetryGuardian` call --
 * a transient read failure would otherwise burn a guardian-quorum attempt
 * slot server-side while the ward only sees a generic error. This panel is
 * the blocking amber state shown instead: no path starts from here, only
 * retry or backing out to a different method.
 *
 * Split out of forensic-recovery-dialog.tsx to keep that file under the
 * 300-line cap (DEVELOPMENT/code-style.md).
 *
 * Adversarial review round 2, MINOR 4: `onRetry` is optional -- "Try again"
 * only renders when the caller actually supplies a retry callback (e.g. the
 * ward's `onRetryLiftQuestions` is wired). Previously the caller always
 * passed a function that silently no-op'd via `?.()` when the real callback
 * was missing, so the button rendered and was clickable even though nothing
 * would happen.
 *
 * Adversarial review round 2, MAJOR 2: `onContinueWithoutQuestions` is
 * likewise optional and renders as a second button only when supplied --
 * the caller wires it only for an ERRORED gating read (never a merely
 * pending one), since a pending read may still resolve to gated and there
 * is nothing safe to fall through to yet. It proceeds along the ungated
 * pre-#3042 path: an explicit user choice, never a silent fall-through, and
 * it does not burn the gating read's own rate budget the way blind
 * "Try again" retries would.
 *
 * Round 3, Major: single-use per dialog open -- a second click when the
 * path is really gated would burn another of the ward's 3 lift attempts
 * (owner ruling 2026-09-15). Once the caller has consumed the hatch it
 * stops passing `onContinueWithoutQuestions` and instead sets `escapeUsed`,
 * which swaps in an explanatory sentence so the button's disappearance
 * doesn't read as a bug.
 */

import { Button } from "@/components/ui/button";

export interface ForensicRecoveryLiftGatePanelProps {
  readonly busy?: boolean;
  readonly onRetry?: () => void;
  readonly onContinueWithoutQuestions?: () => void;
  readonly onChooseDifferentMethod: () => void;
  /** True once "Continue without questions" has already fired this dialog open (round 3, Major). Only meaningful while the hatch itself is withheld. */
  readonly escapeUsed?: boolean;
}

export function ForensicRecoveryLiftGatePanel(
  { busy = false, onRetry, onContinueWithoutQuestions, onChooseDifferentMethod, escapeUsed = false }: ForensicRecoveryLiftGatePanelProps,
): JSX.Element {
  return (
    <div role="alert" className="space-y-4 text-warning">
      <p>
        We could not confirm whether this method needs extra verification questions.
        Try again before continuing -- starting now could use up an approval attempt
        without asking for the answers it needs.
      </p>
      {!onContinueWithoutQuestions && escapeUsed ? (
        <p>You already continued once without questions. Try again or choose a different method.</p>
      ) : null}
      <div className="flex flex-wrap gap-3">
        {onRetry ? (
          <Button type="button" variant="primaryAccessible" loading={busy} onClick={onRetry}>
            Try again
          </Button>
        ) : null}
        {onContinueWithoutQuestions ? (
          <Button type="button" variant="secondary" onClick={onContinueWithoutQuestions}>
            Continue without questions
          </Button>
        ) : null}
        <Button type="button" variant="secondary" onClick={onChooseDifferentMethod}>
          Choose a different method
        </Button>
      </div>
    </div>
  );
}
